import { useMemo, type FC } from 'react';

import type { Employee } from '@pages/Calendar/data';

import type { EventDragState } from '../../AppointmentCard/hooks';

import '../styles/DragPreviewOverlay.scss';

interface DragPreviewOverlayProps {
  dragState: EventDragState;
  employees: Employee[];
  columnWidth: number;
  hasOverflow: boolean;
}

export const DragPreviewOverlay: FC<DragPreviewOverlayProps> = ({
  dragState: {
    targetEmployeeId,
    previewTopPx,
    previewHeightPx,
    previewStart,
    previewEnd,
    clientName,
    phone,
    services,
    previewTitle,
  },
  employees,
  columnWidth,
  hasOverflow,
}) => {
  const targetIndex = useMemo(
    () => employees.findIndex(({ id }) => id === targetEmployeeId),
    [targetEmployeeId, employees]
  );

  const totalEmployees = useMemo(() => employees.length, [employees]);

  const leftPx = useMemo(() => {
    return hasOverflow
      ? targetIndex * (columnWidth + 1) // +1 for the 1px gap between columns
      : (targetIndex / totalEmployees) * 100;
  }, [targetIndex, columnWidth, hasOverflow, totalEmployees]);

  const left = useMemo(() => {
    const suff = hasOverflow ? 'px' : '%';
    return `${leftPx}${suff}`;
  }, [hasOverflow, leftPx]);

  if (targetIndex === -1) {
    return null;
  }

  return (
    <div
      className="DragPreviewOverlay"
      style={{
        top: previewTopPx,
        height: previewHeightPx,
        left,
        width: hasOverflow ? columnWidth : `${100 / totalEmployees}%`,
      }}
      aria-hidden
    >
      <div className="DragPreviewOverlay__header">
        <span className="DragPreviewOverlay__time-range">
          {previewStart} – {previewEnd}
        </span>
      </div>

      <div className="DragPreviewOverlay__body">
        {!!clientName && (
          <div className="DragPreviewOverlay__full-name">{clientName}</div>
        )}
        {!!phone && <div className="DragPreviewOverlay__phone">{phone}</div>}
        {!!services && services.length > 0 ? (
          <div className="DragPreviewOverlay__services">
            {services.map((service) => (
              <div key={service} className="DragPreviewOverlay__service">
                {service}
              </div>
            ))}
          </div>
        ) : (
          <div className="DragPreviewOverlay__title">{previewTitle}</div>
        )}
      </div>
    </div>
  );
};
